// Стык между двумя соседними flight-сегментами варианта — самый информативный элемент
// таймлайна: время стыковки + все предупреждения, которые к нему относятся.
import type { ConnectionInfo } from '../../types';
import { formatDuration, STAY_THRESHOLD_MIN } from '../../core';
import { useT } from '../../i18n';
import styles from './Connection.module.css';

export interface ConnectionProps {
  info: ConnectionInfo;
}

function WarnIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <line x1="12" y1="7" x2="12" y2="14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="12" cy="17.2" r="0.6" fill="currentColor" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

export function Connection({ info }: ConnectionProps) {
  const t = useT();
  const { layoverMin, warnings, hasTransfer, hasLodging } = info;
  // Сутки и больше в одном аэропорту — пребывание (отдых, путешествие, дела),
  // а не стыковка: типичная середина маршрута «туда-обратно».
  const isStay = info.sameAirport && layoverMin !== null && layoverMin >= STAY_THRESHOLD_MIN;

  let carriedNote: string | null = null;
  if (hasTransfer && hasLodging) carriedNote = t('timeline.layover.bothCounted');
  else if (hasTransfer) carriedNote = t('timeline.layover.transferCounted');
  else if (hasLodging) carriedNote = t('timeline.layover.lodgingCounted');

  return (
    <div className={styles.connection} data-connection-gap={info.gapIndex}>
      <div className={styles.rail} aria-hidden="true">
        <div className={styles.dashedLine} />
      </div>
      <div className={styles.body}>
        <div className={styles.headline}>
          {layoverMin === null ? (
            <span className={styles.unknown}>{t('timeline.layover.unknown')}</span>
          ) : (
            <span className={`${styles.duration} mono`}>
              {t(isStay ? 'timeline.stay' : 'timeline.layover', { duration: formatDuration(layoverMin, t) })}
            </span>
          )}
          {carriedNote && <span className={styles.note}>{carriedNote}</span>}
        </div>
        {warnings.length > 0 && (
          <ul className={styles.warnings}>
            {warnings.map((w, i) => (
              <li key={i} className={`${styles.badge} ${w.level === 'error' ? styles.error : styles.warn}`}>
                <WarnIcon />
                <span>{w.message}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
